import {
  Configuration,
  CreatePreferenceDto,
  PreferenceDto,
  PreferencesControllerApiFactory,
  UpdatePreferenceDto,
} from '@ecosystem/preference-filters-api';
import { preferencesEnvironment } from '@environments/environment';
import { ConfigurationParameters } from '@ecosystem/preference-filters-api/dist/configuration';
import { BASE_RETAIL_PATH } from '@utils/UrlUtils';
import { isNotFoundError, isValidationError } from '@utils/userPreferencesUtils/CheckPreferenceErrorStatusCode';
import axios, { AxiosError } from 'axios';
import {
  createAddPreferencesPermissionSuccessfullyResponse,
  createDeletePreferencesPermissionSuccessfullyResponse,
  createInvalidIdResponse,
  createPreferencesCouldNotBeDeletedResponse,
  createPreferencesCouldNotBeSavedResponse,
  createPreferencesDeleteSuccessfullyResponse,
  createPreferencesIdNotFoundResponse,
  createPreferencesNameNotUniqueResponse,
  createPreferencesPermissionCouldNotBeAddedResponse,
  createPreferencesPermissionCouldNotBeDeletedResponse,
  createPreferencesSuccessfullySavedResponse,
} from '@utils/userPreferencesUtils/PreferencesApiResponseFactory';
import saveErrorLog, { ErrorTagEnum } from '@utils/LogUtil';
import {
  AddPreferencePermissionResponse,
  DeletePreferencePermissionResponse,
  DeletePreferenceResponse,
  ErrorStatusResponse,
  Preference,
  PreferenceContext,
  PreferenceResponse,
} from '@utils/userPreferencesUtils/Types';
import { UserAccess } from '@utils/pageConfig/Types';
import { authentication, logger } from '@ecosystem/newron-client-lib';

/**
 * Preferences Api Service is use to call preference filter api which is a platform service to store user preference data
 * example: Category Leaderboard grouping data
 * */

/**
 * @return Preference Object
 *
 * @remarks
 * to map PreferenceDto response to Preference object
 *
 * @param preferenceDto - response object return by preference api
 * **/
export const mapResponseToPreferenceObject = <T>(preferenceDto: PreferenceDto): Preference<T> => {
  //get userId to determine whether preference is owner or not
  const userId = authentication.getUser()?.userId;

  return {
    id: preferenceDto.id,
    key: preferenceDto.key ?? '',
    namespace: preferenceDto.namespace ?? '',
    type: preferenceDto.type,
    values: preferenceDto.data as T,
    //True if the preference was belonged to the login user.
    isPreferenceOwner: userId === preferenceDto.created_by,
  };
};

/**
 * @return a Promise of PreferenceResponse object
 *
 * @remarks
 * function to create a user preference data and store it in platform remote database
 *
 * @param userToken  - user bearer token
 * @param context    - Preference Context contains type, namespace and key(group name)
 * @param data       - preference data with a generic data type (object data that you wanted to store for respective page)
 * **/
export const createPreferences = async <T extends Record<string, any>>(
  userToken: string,
  context: PreferenceContext,
  data: T,
): Promise<PreferenceResponse<T>> => {
  const [retailerName] = context.namespace.split(':');
  if (!retailerName) {
    const errorMsg = `Retailer name missing in namespace: "${context.namespace}"`;
    logger.error(new Error(errorMsg), {
      category: ErrorTagEnum.ApiError,
      clientId: data?.clientId.trim() ? data.clientId : authentication.getUser()?.clientId,
    });
    return createPreferencesCouldNotBeSavedResponse(context.key);
  }
  const requestParam: CreatePreferenceDto = {
    key: context.key,
    namespace: context.namespace,
    data,
  };

  const preferencesService = PreferencesControllerApiFactory(
    new Configuration({
      accessToken: userToken,
      basePath: preferencesEnvironment.baseUrl,
    } as ConfigurationParameters),
  );

  try {
    const preference = await preferencesService.preferencesControllerCreate(
      BASE_RETAIL_PATH,
      context.type,
      requestParam,
    );

    return createPreferencesSuccessfullySavedResponse<T>(
      context.namespace,
      mapResponseToPreferenceObject<T>(preference.data),
    );
  } catch (e) {
    if (axios.isAxiosError(e) && isValidationError(e as AxiosError<ErrorStatusResponse>)) {
      return createPreferencesNameNotUniqueResponse();
    }

    saveErrorLog(e, `Failed to save preference ${context.key} for ${context.namespace}`, {
      category: ErrorTagEnum.ApiError,
    });

    return createPreferencesCouldNotBeSavedResponse(context.key);
  }
};

/**
 * @return a Promise of an array of PreferenceResponse object
 *
 * @remarks
 * function to get an array of preference data from platform remote database
 *
 * @param userToken  - user bearer token
 * @param type       - to difference the different pages (example: "category-leaderboard-group" for Category Leaderboard Page)
 * @param namespace  - combination of retailer client and country
 * **/
export const getPreferences = async <T>(
  userToken: string,
  type: string,
  namespace: string,
): Promise<Preference<T>[]> => {
  const preferencesService = PreferencesControllerApiFactory(
    new Configuration({
      accessToken: userToken,
      basePath: preferencesEnvironment.baseUrl,
    } as ConfigurationParameters),
  );
  const undefinedNamespace = `undefined::${namespace.split('::')[1]}`;

  const [undefinedNamespaceResponse, properNamespaceResponse] = await Promise.all([
    //To handle ancient bug from product page, that we used to pass namespace as undefined::DE to preference API.
    //After we fix the namespace with proper client name, those saved group cannot visible anymore due to different namespace that we sent.
    //Therefore, we are forced to concat both for example: namespace=undefined::DE plus namespace=Amazon::DE, to make sure existing and new saved group won't get impacted.
    preferencesService.preferencesControllerFindAll(BASE_RETAIL_PATH, type, undefinedNamespace, true),
    preferencesService.preferencesControllerFindAll(BASE_RETAIL_PATH, type, namespace, true),
  ]);

  const combinedData = [...(undefinedNamespaceResponse.data ?? []), ...(properNamespaceResponse.data ?? [])];

  return combinedData.map((p) => mapResponseToPreferenceObject<T>(p));
};

/**
 * @return a Promise of PreferenceResponse object
 *
 * @remarks
 * function to update the existing preference data store in platform remote database
 *
 * @param userToken  - user bearer token
 * @param id         - id of the preference data record in database
 * @param context    - Preference Context contains type, namespace and key(group name)
 * @param data       - preference data with a generic data type (object data that you wanted to store for respective page)
 *  **/
export const updatePreferences = async <T extends Record<string, any>>(
  userToken: string,
  id: string,
  context: PreferenceContext,
  data: T,
): Promise<PreferenceResponse<T>> => {
  if (!id) {
    return createInvalidIdResponse(id);
  }

  const requestParam: UpdatePreferenceDto = {
    key: context.key,
    namespace: context.namespace,
    data,
  };

  const preferencesService = PreferencesControllerApiFactory(
    new Configuration({
      accessToken: userToken,
      basePath: preferencesEnvironment.baseUrl,
    } as ConfigurationParameters),
  );

  try {
    const preference = await preferencesService.preferencesControllerUpdateById(
      BASE_RETAIL_PATH,
      context.type,
      id,
      requestParam,
    );

    return createPreferencesSuccessfullySavedResponse<T>(
      context.namespace,
      mapResponseToPreferenceObject<T>(preference.data),
    );
  } catch (e) {
    if (axios.isAxiosError(e) && isValidationError(e as AxiosError<ErrorStatusResponse>)) {
      return createPreferencesNameNotUniqueResponse();
    }

    saveErrorLog(e, `Failed to save preference ${context.key} for ${context.namespace}`, {
      category: ErrorTagEnum.ApiError,
    });

    return createPreferencesCouldNotBeSavedResponse(context.key);
  }
};

/**
 * @return a Promise of DeletePreferenceResponse object
 *
 * @remarks
 * function to delete the existing preference data store in platform remote database
 *
 * @param userToken  - user bearer token
 * @param type       - to differenciate different pages (example: "category-leaderboard-group" for Category Leaderboard Page)
 * @param id         - id of the preference data record in database
 *  **/
export const deletePreferences = async (
  userToken: string,
  type: string,
  id: string,
): Promise<DeletePreferenceResponse> => {
  if (!id) {
    return createInvalidIdResponse(id);
  }

  const preferencesService = PreferencesControllerApiFactory(
    new Configuration({
      accessToken: userToken,
      basePath: preferencesEnvironment.baseUrl,
    } as ConfigurationParameters),
  );

  try {
    const preference = await preferencesService.preferencesControllerDeleteById(BASE_RETAIL_PATH, type, id);

    return createPreferencesDeleteSuccessfullyResponse(preference.data.key);
  } catch (e) {
    if (axios.isAxiosError(e) && isNotFoundError(e as AxiosError<ErrorStatusResponse>, id)) {
      return createPreferencesIdNotFoundResponse(id);
    }

    saveErrorLog(e, `Failed to delete preference ${id}`, {
      category: ErrorTagEnum.ApiError,
    });

    return createPreferencesCouldNotBeDeletedResponse();
  }
};

export const getPreferenceInfoByGroupName = async (
  token: string,
  groupName: string,
  type: string,
  namespace: string,
) => {
  const preferences = await getPreferences(token, type, namespace);
  return preferences.find((preference) => preference.namespace === namespace && preference.key === groupName);
};

/**
 * Function to get permissions for a preference
 * @param key group name
 * @param type preference type name
 * @param namespace - combination of retailer client and country
 * @returns list of users with their access level
 */
export const getPreferencesPermissions = async (
  key: string,
  type: string,
  namespace: string,
): Promise<UserAccess[]> => {
  const token = await authentication.getAccessToken();

  const currentPreference = await getPreferenceInfoByGroupName(token, key, type, namespace);

  const { data } = await axios.get<UserAccess[]>(
    `${preferencesEnvironment.baseUrl}/v1/preferences/${BASE_RETAIL_PATH}/${type}/${currentPreference?.id}/permissions`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return data;
};

/**
 * Function to remove user from a preference
 * Seems like we don't need to use this if we are using ShareModal from client-lib
 * @param type - to differenciate different pages (example: "category-leaderboard-group" for Category Leaderboard Page)
 * @param preferenceId preference id
 * @param userId user id to be removed
 * @param key group name
 * @returns returns OK if successful
 */
export const deletePreferencesPermissions = async (
  type: string,
  preferenceId: string,
  userId: string,
  key: string,
): Promise<DeletePreferencePermissionResponse> => {
  try {
    const token = await authentication.getAccessToken();
    await axios.delete(
      `${preferencesEnvironment.baseUrl}/v1/preferences/${BASE_RETAIL_PATH}/${type}/${preferenceId}/permissions/${userId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    return createDeletePreferencesPermissionSuccessfullyResponse(key);
  } catch (err) {
    if (axios.isAxiosError(err) && isNotFoundError(err as AxiosError<ErrorStatusResponse>, preferenceId)) {
      return createPreferencesIdNotFoundResponse(preferenceId);
    }

    saveErrorLog(err, `Error from delete preference permission from ${preferenceId}`, {
      category: ErrorTagEnum.ApiError,
    });

    return createPreferencesPermissionCouldNotBeDeletedResponse();
  }
};

/**
 * Function to add permissions to a preference
 * @param key group Name
 * @param type - to differenciate different pages (example: "category-leaderboard-group" for Category Leaderboard Page)
 * @param namespace - combination of retailer client and country
 * @param permissions list of users with their access level to be sent as body, owner must be included
 * @returns returns OK if successful
 */
export const addPreferencesPermissions = async (
  key: string,
  type: string,
  namespace: string,
  permissions: any,
): Promise<AddPreferencePermissionResponse> => {
  const token = await authentication.getAccessToken();
  const currentPreference = await getPreferenceInfoByGroupName(token, key, type, namespace);
  try {
    await axios.patch<string>(
      `${preferencesEnvironment.baseUrl}/v1/preferences/${BASE_RETAIL_PATH}/${type}/${currentPreference?.id}/permissions`,
      permissions,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return createAddPreferencesPermissionSuccessfullyResponse(key);
  } catch (err) {
    if (
      axios.isAxiosError(err) &&
      isNotFoundError(err as AxiosError<ErrorStatusResponse>, currentPreference?.id ?? key)
    ) {
      return createPreferencesIdNotFoundResponse(currentPreference?.id ?? key);
    }

    saveErrorLog(err, `Error from add preference permission from ${currentPreference?.id ?? key}`, {
      category: ErrorTagEnum.ApiError,
    });

    return createPreferencesPermissionCouldNotBeAddedResponse();
  }
};

export const PreferenceApiService = {
  createPreferences,
  getPreferences,
  updatePreferences,
  deletePreferences,
  getPreferencesPermissions,
  deletePreferencesPermissions,
  addPreferencesPermissions,
};
